/**
 * Keyboard-shortcuts help overlay for the facility-map editor.
 *
 * A centered modal listing all editor shortcuts, opened with "?" and closed
 * with Escape / the close button / backdrop click. Purely presentational.
 */

import { X } from "lucide-react";

const MOD = navigator.platform.toLowerCase().includes("mac") ? "⌘" : "Ctrl";

const GROUPS: Array<{ title: string; items: Array<[string, string]> }> = [
  {
    title: "View",
    items: [
      [`${MOD} + Scroll`, "Zoom at cursor"],
      ["Scroll / Trackpad", "Pan"],
      ["Space + Drag", "Pan"],
    ],
  },
  {
    title: "Edit",
    items: [
      [`${MOD} + Z`, "Undo"],
      [`${MOD} + Shift + Z`, "Redo"],
      [`${MOD} + D`, "Duplicate selection"],
      ["Delete / Backspace", "Delete selection"],
      ["Arrow keys", "Nudge selection"],
      ["Shift + Arrows", "Nudge by 10"],
    ],
  },
  {
    title: "Draw",
    items: [
      ["Click", "Place a vertex"],
      ["Enter / dbl-click", "Finish line"],
      ["Backspace", "Remove last vertex"],
      ["Shift", "Straight (ortho) segment"],
      ["Type a number", "Exact segment length"],
      ["Escape", "Cancel drawing"],
    ],
  },
];

function ShortcutsOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-raised)] p-5 shadow-[var(--shadow-xl)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            Keyboard shortcuts
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                {g.title}
              </p>
              <div className="space-y-1.5">
                {g.items.map(([key, desc]) => (
                  <div key={key} className="text-xs">
                    <kbd className="inline-block rounded border border-[var(--border-default)] bg-[var(--surface-sunken)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--text-secondary)]">
                      {key}
                    </kbd>
                    <span className="ml-1.5 text-[var(--text-secondary)]">
                      {desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ShortcutsOverlay;
